import React, { useEffect, useMemo, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { fetchSubTasksByTaskId } from "../subTaskService";
import { fetchAllTasks } from "../taskService";
import {
  fetchAllUsers,
  fetchIssueById,
  fetchIssueStatuses,
  issueDueDateToInputValue,
  updateIssue
} from "./issueService";
import IssueFileAttachments from "./IssueFileAttachments";
import { fetchTestCases } from "./testCaseService";

const ISSUE_TYPES = ["Bug", "Task", "Improvement"];
const ISSUE_LAYERS = ["Frontend", "Backend", "Database", "API", "DevOps", "Other"];
const ISSUE_PRIORITIES = ["High", "Medium", "Low"];

const FALLBACK_STATUSES = [
  { id: 1, name: "Open" },
  { id: 2, name: "In Progress" },
  { id: 3, name: "Resolved" },
  { id: 4, name: "Closed" }
];

function parseIdList(text) {
  if (!text) return [];
  return String(text)
    .split(/[,\s]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function testCaseIdsTextFromIssue(iss) {
  if (Array.isArray(iss?.Test_Case_Ids) && iss.Test_Case_Ids.length) {
    return iss.Test_Case_Ids.join(", ");
  }
  if (Array.isArray(iss?.Test_case_Ids) && iss.Test_case_Ids.length) {
    return iss.Test_case_Ids.join(", ");
  }
  return "";
}

export default function IssueEditForm({ issueId, issue, canEdit = true, onUpdated, onIssueRefreshed }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAssigneesDropdown, setShowAssigneesDropdown] = useState(false);

  const [statuses, setStatuses] = useState(FALLBACK_STATUSES);
  const [loadingStatuses, setLoadingStatuses] = useState(true);

  const [subtasks, setSubtasks] = useState([]);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);

  const [testCases, setTestCases] = useState([]);
  const [loadingTestCases, setLoadingTestCases] = useState(false);
  const [showTestCasesDropdown, setShowTestCasesDropdown] = useState(false);
  const [testCaseSearchQuery, setTestCaseSearchQuery] = useState("");

  const initialAssigned = useMemo(() => {
    const assignedUsers = Array.isArray(issue?.Assigned_Users) ? issue.Assigned_Users : [];
    return assignedUsers
      .map((u) => u.User_Id ?? u.user_id ?? u.id)
      .filter((x) => x != null);
  }, [issue]);

  const [formData, setFormData] = useState(() => ({
    taskId: issue?.Task_Id ? String(issue.Task_Id) : "",
    subTaskId: issue?.Sub_Task_Id ? String(issue.Sub_Task_Id) : "",
    title: issue?.Title ?? "",
    description: issue?.Description ?? "",
    type: issue?.Type ?? "Bug",
    issueLayer: issue?.Issue_Layer ?? "Backend",
    statusId: issue?.Status_Id != null ? String(issue.Status_Id) : "2",
    priority: issue?.Priority ?? "Low",
    dueDate: issueDueDateToInputValue(issue?.Due_Date),
    actualEndDate: issueDueDateToInputValue(
      issue?.Actual_End_Date ??
        issue?.actual_end_date ??
        issue?.Updated_Date ??
        issue?.updated_date ??
        issue?.Updated_On ??
        issue?.updated_on
    ),
    assignedUserIds: initialAssigned,
    testCaseIdsText: testCaseIdsTextFromIssue(issue)
  }));

  // Keep form in sync when issue loads
  useEffect(() => {
    if (!issue) return;
    setFormData({
      taskId: issue?.Task_Id ? String(issue.Task_Id) : "",
      subTaskId: issue?.Sub_Task_Id ? String(issue.Sub_Task_Id) : "",
      title: issue?.Title ?? "",
      description: issue?.Description ?? "",
      type: issue?.Type ?? "Bug",
      issueLayer: issue?.Issue_Layer ?? "Backend",
      statusId: issue?.Status_Id != null ? String(issue.Status_Id) : "2",
      priority: issue?.Priority ?? "Low",
      dueDate: issueDueDateToInputValue(issue?.Due_Date),
      actualEndDate: issueDueDateToInputValue(
        issue?.Actual_End_Date ??
          issue?.actual_end_date ??
          issue?.Updated_Date ??
          issue?.updated_date ??
          issue?.Updated_On ??
          issue?.updated_on
      ),
      assignedUserIds: initialAssigned,
      testCaseIdsText: testCaseIdsTextFromIssue(issue)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId, issue]);

  useEffect(() => {
    const loadTasks = async () => {
      setLoadingTasks(true);
      try {
        const list = await fetchAllTasks();
        const mapped = (list || [])
          .map((t) => ({
            id: t.Task_Id ?? t.task_id ?? t.id,
            title: t.Title ?? t.title ?? t.Task_Title ?? t.task_title ?? "—"
          }))
          .filter((x) => x.id != null);
        mapped.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setTasks(mapped);
      } catch (err) {
        console.error("Error loading tasks:", err);
        setTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    };
    loadTasks();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const list = await fetchAllUsers();
        const sorted = (list || []).sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setUsers(sorted);
      } catch (err) {
        console.error("Error loading users:", err);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const loadStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const list = await fetchIssueStatuses();
        const mapped = (list || [])
          .map((s) => ({ id: s.id, name: s.name, description: s.description }))
          .filter((x) => x.id != null);
        mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setStatuses(mapped.length ? mapped : FALLBACK_STATUSES);
      } catch (err) {
        console.error("Error loading issue statuses:", err);
        setStatuses(FALLBACK_STATUSES);
      } finally {
        setLoadingStatuses(false);
      }
    };
    loadStatuses();
  }, []);

  useEffect(() => {
    const closeDropdown = (e) => {
      if (!e.target.closest(".form-group-multi-select")) {
        setShowAssigneesDropdown(false);
        setShowTestCasesDropdown(false);
      }
    };
    document.addEventListener("click", closeDropdown);
    return () => document.removeEventListener("click", closeDropdown);
  }, [showAssigneesDropdown, showTestCasesDropdown]);

  useEffect(() => {
    const loadSubtasks = async () => {
      const taskIdNum = Number(formData.taskId);
      if (!taskIdNum || !Number.isFinite(taskIdNum)) {
        setSubtasks([]);
        setFormData((prev) => ({ ...prev, subTaskId: "", testCaseIdsText: "" }));
        return;
      }
      setLoadingSubtasks(true);
      try {
        const list = await fetchSubTasksByTaskId(taskIdNum);
        const mapped = (list || [])
          .map((st) => ({
            id: st.Sub_Task_Id ?? st.sub_task_id ?? st.id,
            title: st.Title ?? st.title ?? "—"
          }))
          .filter((x) => x.id != null);
        setSubtasks(mapped);
      } catch (err) {
        console.error("Error loading subtasks:", err);
        setSubtasks([]);
      } finally {
        setLoadingSubtasks(false);
      }
    };
    loadSubtasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.taskId]);

  const allUsersSelected = !loadingUsers && users.length > 0 && formData.assignedUserIds.length === users.length;
  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => ({
      ...prev,
      assignedUserIds: allUsersSelected ? [] : users.map((u) => u.id)
    }));
  };

  const handleUserToggle = (userId) => {
    setFormData((prev) => {
      const ids = prev.assignedUserIds.includes(userId)
        ? prev.assignedUserIds.filter((id) => id !== userId)
        : [...prev.assignedUserIds, userId];
      return { ...prev, assignedUserIds: ids };
    });
  };

  const statusOptions = useMemo(() => statuses, [statuses]);
  const selectedTestCaseIds = useMemo(
    () => parseIdList(formData.testCaseIdsText),
    [formData.testCaseIdsText]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "taskId") {
        return { ...prev, taskId: value, subTaskId: "", testCaseIdsText: "" };
      }
      if (name === "subTaskId") {
        return { ...prev, subTaskId: value, testCaseIdsText: "" };
      }
      return { ...prev, [name]: value };
    });
  };

  // Load test cases when Task/Sub Task changes
  useEffect(() => {
    const taskIdNum = Number(formData.taskId);
    if (!taskIdNum || !Number.isFinite(taskIdNum)) {
      setTestCases([]);
      setTestCaseSearchQuery("");
      setShowTestCasesDropdown(false);
      return;
    }

    const subTaskIdNum = formData.subTaskId ? Number(formData.subTaskId) : null;
    let cancelled = false;
    setLoadingTestCases(true);
    setShowTestCasesDropdown(false);
    setTestCaseSearchQuery("");

    fetchTestCases({ taskId: taskIdNum, subTaskId: subTaskIdNum })
      .then((list) => {
        if (cancelled) return;
        const mapped = (list || [])
          .map((tc) => ({
            id: tc.Test_Case_Id ?? tc.test_case_id ?? tc.id,
            title:
              tc.Title ??
              tc.title ??
              tc.Test_Case_Title ??
              tc.test_case_title ??
              "—",
          }))
          .filter((x) => x.id != null);
        mapped.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setTestCases(mapped);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error loading test cases:", err);
        setTestCases([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingTestCases(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formData.taskId, formData.subTaskId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    const taskId = Number(formData.taskId);
    const subTaskId = formData.subTaskId ? Number(formData.subTaskId) : null;
    const statusId = Number(formData.statusId);
    const title = (formData.title || "").trim();
    const description = (formData.description || "").trim();
    const testCaseIds = parseIdList(formData.testCaseIdsText);

    if (!taskId || !title) {
      setError("Task and Title are required.");
      return;
    }
    if (!statusId) {
      setError("Status is required.");
      return;
    }

    setError("");
    const payload = {
      Task_Id: taskId,
      Title: title,
      Description: description || null,
      Type: formData.type || "Bug",
      Issue_Layer: formData.issueLayer || "Backend",
      Status_Id: statusId,
      Priority: formData.priority || "Low",
      Assigned_User_Ids: (formData.assignedUserIds || []).map((id) => Number(id)),
      // Use the same field as create/get to match backend validation.
      Test_Case_Ids: testCaseIds
    };
    if (subTaskId) payload.Sub_Task_Id = subTaskId;
    const dueTrim = (formData.dueDate || "").trim();
    if (dueTrim) payload.Due_Date = dueTrim.slice(0, 10);
    const actualEndTrim = (formData.actualEndDate || "").trim();
    if (actualEndTrim) payload.Actual_End_Date = actualEndTrim.slice(0, 10);

    setSubmitting(true);
    try {
      const updated = await updateIssue(issueId, payload);
      let next = null;
      try {
        next = await fetchIssueById(issueId);
      } catch (refetchErr) {
        console.error("Error refetching issue after update:", refetchErr);
      }
      if (onUpdated) onUpdated(next ?? updated);
    } catch (err) {
      console.error("Error updating issue:", err);
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to update issue.");
    } finally {
      setSubmitting(false);
    }
  };

  const refreshIssueData = async () => {
    try {
      const fresh = await fetchIssueById(issueId);
      if (fresh && onIssueRefreshed) onIssueRefreshed(fresh);
    } catch (err) {
      console.error("Error refreshing issue:", err);
    }
  };

  return (
    <form className="issues-form" onSubmit={handleSubmit}>
     

      {!canEdit ? (
        <div className="issues-alert">
          You don’t have permission to edit issues.
        </div>
      ) : null}

      {error ? <div className="issues-alert issues-alert-error">{error}</div> : null}

      <div className="issues-form-grid">
        <div className="form-group">
          <label>Task <span className="required">*</span></label>
          <select
            name="taskId"
            value={formData.taskId}
            onChange={handleInputChange}
            required
            disabled={!canEdit || loadingTasks}
          >
            <option value="">
              {loadingTasks ? "Loading tasks..." : "Select Task"}
            </option>
            {tasks.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.title}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Sub Task</label>
          <select
            name="subTaskId"
            value={formData.subTaskId}
            onChange={handleInputChange}
            disabled={!canEdit || !formData.taskId || loadingSubtasks}
          >
            <option value="">
              {loadingSubtasks
                ? "Loading subtasks..."
                : subtasks.length
                  ? "Select sub task (optional)"
                  : "No sub tasks for this task"}
            </option>
            {subtasks.map((st) => (
              <option key={st.id} value={st.id}>
                {st.title} (#{st.id})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Test Cases</label>

          <div className="form-group-multi-select" style={{ width: "100%", position: "relative" }}>
            <div
              className="permissions-select-trigger"
              onClick={() => {
                if (!canEdit) return;
                if (!formData.taskId) return;
                if (!loadingTestCases) setShowTestCasesDropdown((prev) => !prev);
              }}
              style={{
                opacity: !canEdit || loadingTestCases || !formData.taskId ? 0.6 : 1,
                cursor: !canEdit || loadingTestCases || !formData.taskId ? "default" : "pointer",
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!canEdit) return;
                  if (!formData.taskId) return;
                  if (!loadingTestCases) setShowTestCasesDropdown((prev) => !prev);
                }
              }}
            >
              <span className="permissions-select-text">
                {loadingTestCases
                  ? "Loading test cases..."
                  : selectedTestCaseIds.length === 0
                    ? formData.taskId
                      ? "Select test cases"
                      : "Select task first"
                    : `${selectedTestCaseIds.length} test case${selectedTestCaseIds.length > 1 ? "s" : ""} selected`}
              </span>
              <FaChevronDown
                className={`permissions-select-chevron ${showTestCasesDropdown ? "open" : ""}`}
              />
            </div>

            {showTestCasesDropdown && !loadingTestCases && canEdit && formData.taskId && (
              <div
                className="permissions-select-panel"
                style={{ maxHeight: 240, overflowY: "auto" }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  placeholder="Search test cases..."
                  value={testCaseSearchQuery}
                  onChange={(e) => setTestCaseSearchQuery(e.target.value)}
                  style={{
                    margin: "8px",
                    width: "calc(100% - 16px)",
                    padding: "10px 12px",
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />

                {(testCaseSearchQuery.trim()
                  ? testCases.filter((tc) =>
                      String(tc.title || "")
                        .toLowerCase()
                        .includes(testCaseSearchQuery.trim().toLowerCase())
                    )
                  : testCases
                ).map((tc) => (
                  <label key={tc.id} className="permissions-select-item">
                    <input
                      type="checkbox"
                      checked={selectedTestCaseIds.includes(tc.id)}
                      onChange={() => {
                        const next = selectedTestCaseIds.includes(tc.id)
                          ? selectedTestCaseIds.filter((id) => id !== tc.id)
                          : [...selectedTestCaseIds, tc.id];
                        setFormData((prev) => ({
                          ...prev,
                          testCaseIdsText: next.join(", "),
                        }));
                      }}
                    />
                    <span className="permissions-select-item-label">{tc.title}</span>
                    <span style={{ color: "#97a0af", fontSize: 12, whiteSpace: "nowrap" }}>
                      #{tc.id}
                    </span>
                  </label>
                ))}

                {(
                  (testCaseSearchQuery.trim()
                    ? testCases.filter((tc) =>
                        String(tc.title || "")
                          .toLowerCase()
                          .includes(testCaseSearchQuery.trim().toLowerCase())
                      )
                    : testCases
                  ).length === 0
                ) ? (
                  <div style={{ padding: 12, fontSize: 13, color: "#5E6C84", textAlign: "center" }}>
                    No test cases found
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {selectedTestCaseIds.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {selectedTestCaseIds.map((id) => {
                const tc = testCases.find((t) => String(t.id) === String(id));
                const label = tc?.title || `#${id}`;
                return (
                  <span
                    key={id}
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: "#E3F2FD",
                      color: "#1565C0",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="issues-field-hint" style={{ marginTop: 6 }}>
               Select one or more test cases.
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Title <span className="required">*</span></label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Issue title"
            required
            disabled={!canEdit}
          />
        </div>

        <div className="form-group">
          <label>Type</label>
          <select name="type" value={formData.type} onChange={handleInputChange} disabled={!canEdit}>
            {ISSUE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Issue Layer</label>
          <select name="issueLayer" value={formData.issueLayer} onChange={handleInputChange} disabled={!canEdit}>
            {ISSUE_LAYERS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            name="statusId"
            value={formData.statusId}
            onChange={handleInputChange}
            disabled={!canEdit || loadingStatuses}
          >
            {loadingStatuses ? <option value={formData.statusId}>Loading statuses...</option> : null}
            {statusOptions.map((s) => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Priority</label>
          <select name="priority" value={formData.priority} onChange={handleInputChange} disabled={!canEdit}>
            {ISSUE_PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Due date</label>
          <input
            type="date"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleInputChange}
            disabled={!canEdit}
          />
          <div className="issues-field-hint" style={{ marginTop: 6 }}>Optional.</div>
        </div>

        <div className="form-group">
          <label>Actual end date</label>
          <input
            type="date"
            name="actualEndDate"
            value={formData.actualEndDate}
            onChange={handleInputChange}
            disabled={!canEdit}
          />
        </div>

        <div className="form-group form-group-multi-select">
          <label>Assigned Users</label>
          <div style={{ width: "100%" }}>
            <div
              className="permissions-select-trigger"
              onClick={() => {
                if (!canEdit) return;
                if (!loadingUsers) setShowAssigneesDropdown((prev) => !prev);
              }}
              style={{
                opacity: (!canEdit || loadingUsers) ? 0.6 : 1,
                cursor: (!canEdit || loadingUsers) ? "default" : "pointer"
              }}
            >
              <span className="permissions-select-text">
                {loadingUsers
                  ? "Loading users..."
                  : formData.assignedUserIds.length === 0
                    ? "Select assignees"
                    : `${formData.assignedUserIds.length} user${formData.assignedUserIds.length > 1 ? "s" : ""} selected`}
              </span>
              <FaChevronDown className={`permissions-select-chevron ${showAssigneesDropdown ? "open" : ""}`} />
            </div>

            {showAssigneesDropdown && !loadingUsers && canEdit && (
              <div
                className="permissions-select-panel"
                style={{ maxHeight: 240, overflowY: "auto" }}
                onClick={(e) => e.stopPropagation()}
              >
                <label className="permissions-select-item">
                  <input
                    type="checkbox"
                    checked={allUsersSelected}
                    onChange={handleToggleSelectAllUsers}
                  />
                  <span className="permissions-select-item-label">Select all</span>
                </label>
                {users.map((u) => (
                  <label key={u.id} className="permissions-select-item">
                    <input
                      type="checkbox"
                      checked={formData.assignedUserIds.includes(u.id)}
                      onChange={() => handleUserToggle(u.id)}
                    />
                    <span className="permissions-select-item-label">{u.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group issues-description-full">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            placeholder="Issue description"
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="viewlead-card issue-attachments-card issues-form-attachments-wrap">
        <IssueFileAttachments
          issue={issue}
          issueId={issue?.Issue_Id ?? issueId}
          canUpload={canEdit}
          onRefresh={refreshIssueData}
        />
      </div>

      <div className="issues-form-actions">
        <button type="submit" className="btn-add-lead" disabled={!canEdit || submitting}>
          {submitting ? "Updating..." : "Update Issue"}
        </button>
      </div>
    </form>
  );
}

